import { htmlEncode } from '../../utils/escape.utils';
import { formatPercentages, formatPercentageValue } from '../../utils/formatter.utils';
import { LocaleManager } from '@nirvana/shared/i18n-locale/common';
import { GetOverviewInformationResponse, Status } from '@nirvana/shared/dtos/types/widgets/common';
import { DisplayMode, TemplateState } from '../../types/common/types';
import {
  AlertsNavigationParams,
  NavigationConstants,
  NavigationHelper,
  NavigationItem,
  OverviewNavigationHelper,
  RiskManagementNavigationParams,
} from '@nirvana/shared/navigation';
import { BaseWidgetView } from '../../widget/BaseWidgetView';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { BackendResponseWithMeta, DASHBOARD_METADATA_KEY } from '../../types/widget/types';
import { GzButtonWebComponent } from '../../ui/gzButton';
import { ButtonColors, ButtonHeights, ButtonTypes } from '../../ui/gzButton/types';
import { OverviewEvents, OverviewNavigationParams } from '../types/OverviewTypes';
import { DashboardWebComponent } from '../../dashboard';
import { Rights } from '@nirvana/shared/constants';
import { setElementClass } from '../../utils/htmlElement.utils';
import { GzTooltipWebComponent } from '../../ui/gzTooltip';
import { GzDashboardMacroComponentWebComponent } from '../../genericDashboard/gzDashboardMacroComponent';
import { exitFullscreen, isBrowserFullscreen, onExitFullscreenError } from '../../utils/fullscreen.utils';
import {
  ManagementStatus,
  NetworkColumn,
  NetworkGridAppExternalParams,
} from '../../networkV2/networkAppContainer/helpers/types';
import { ExecutiveSummaryDrillDownHelper } from '../../networkV2/networkAppContainer/helpers/drillDowns/executive.summary.drill.down.helper';
import { Filter as GridFilter } from '../../grid/gridFilters/types';

enum OverviewItems {
  MANAGED_ENDPOINTS = 'managedEndpoints',
  ACTIVE_ENDPOINTS = 'activeEndpoints',
  THREATS_BLOCKED = 'threatsBlocked',
  COMPANY_SCORE = 'companyScore',
}

export class OverviewView extends BaseWidgetView {
  public latestData: GetOverviewInformationResponse['widgetResult'];
  public shadow: any;
  protected translations: any;
  private readonly localeManager: LocaleManager = new LocaleManager();
  private navigationHandlers: Array<(event: any) => void> = [];
  private shouldDisplayInstallButtons: boolean;
  private shouldDisplayIconsButtons: boolean;
  private iconButtonsDisplayed: boolean;
  private installButtonsDisplayed: boolean;
  private networkDisplayedColumns: Array<NetworkColumn> = [
    NetworkColumn.NAME,
    NetworkColumn.COMPANY,
    NetworkColumn.MANAGEMENT_STATUS,
    NetworkColumn.LAST_SEEN,
    NetworkColumn.TYPE,
    NetworkColumn.ROLE,
    NetworkColumn.OS_TYPE,
    NetworkColumn.OS_VERSION,
    NetworkColumn.ENDPOINT_TYPE,
  ];

  getDomElements() {
    this.dom.$itemsList = this.shadow.querySelectorAll('.item');
    this.dom.$managedEndpointsTitle = this.shadow.querySelector('#managedEndpointsTitle');
    this.dom.$managedEndpointsContainer = this.shadow.querySelector(`[gz-overview="managedEndpoints"]`);
    this.dom.$activeEndpointsTitle = this.shadow.querySelector('#activeEndpointsTitle');
    this.dom.$threatsBlockedTitle = this.shadow.querySelector('#threatsBlockedTitle');
    this.dom.$companyScoreTitle = this.shadow.querySelector('#companyScoreTitle');
    this.dom.$managedEndpointsEmpty = this.shadow.querySelector('#managedEndpointsEmpty');
    this.dom.$managedEndpointsEmptyStateText = this.shadow.querySelector('.managedEndpointsText');
    this.dom.$managedEndpointsEmptyState = this.shadow.querySelector('#managedEndpointsButtons');
    this.dom.$installButtonsContainer = this.shadow.querySelector('#installButtonsContainer');
    this.getTranslations();
    this.setTranslatedStrings();
    this.setErrorTooltip();
  }

  addDrillDownHandlers(
    targets: Array<Target>,
    timeRange: number,
    canDrillDown: boolean,
    notAvailableFeatures: string[] = []
  ) {
    if (!NavigationHelper.shouldAddHandlers(this.dependencies.Ext)) {
      return;
    }

    const selectedTargetPath = targets[0].path;

    if (!selectedTargetPath) {
      throw new Error('Missing path from target.');
    }

    const isPartner = Application.Main.company.isPartner();

    if (!isPartner) {
      this.networkDisplayedColumns.splice(this.networkDisplayedColumns.indexOf(NetworkColumn.COMPANY), 1);
    }

    let navigationItems: Array<NavigationItem<OverviewNavigationParams>> = [
      {
        elements: [
          this.shadow.querySelector(`[gz-overview="managedEndpoints"] .widget-title`),
          this.shadow.querySelector(`[gz-overview="managedEndpoints"] [gz-overview-field="value"]`),
        ],
        section: NavigationConstants.NETWORK_V2.NAME,
        params: {
          filters: this.getManagedEndpointsFilters(targets, isPartner),
          treeDetails: {
            targetId: targets[0].companyId,
            type: Constants.Companies.TYPE_COMPANY,
            path: selectedTargetPath,
            parentId: '', // not required
          },
          displayedColumns: this.networkDisplayedColumns,
          recursive: true,
        },
      },
      {
        elements: [
          this.shadow.querySelector(`[gz-overview="activeEndpoints"] .widget-title`),
          this.shadow.querySelector(`[gz-overview="activeEndpoints"] [gz-overview-field="value"]`),
        ],
        section: NavigationConstants.NETWORK_V2.NAME,
        params: {
          filters: this.getActiveEndpointsFilters(targets, timeRange, isPartner),
          treeDetails: {
            targetId: targets[0].companyId,
            type: Constants.Companies.TYPE_COMPANY,
            path: selectedTargetPath,
            parentId: '', // not required
          },
          displayedColumns: this.networkDisplayedColumns,
          recursive: true,
        },
      },
      {
        elements: [
          this.shadow.querySelector(`[gz-overview="companyScore"] .widget-title`),
          this.shadow.querySelector(`[gz-overview="companyScore"] [gz-overview-field="value"]`),
        ],
        section: NavigationConstants.RISK_MANAGEMENT.NAME,
        params: <RiskManagementNavigationParams>{
          company: {
            id: targets[0].companyId,
            name: targets[0].companyName,
          },
        },
      },
      {
        elements: [
          this.shadow.querySelector(`[gz-overview="threatsBlocked"] .widget-title`),
          this.shadow.querySelector(`[gz-overview="threatsBlocked"] [gz-overview-field="value"]`),
        ],
        section: NavigationConstants.ALERTS.NAME,
        params: {
          displayedColumns: OverviewNavigationHelper.getDisplayedColumns(),
          filters: OverviewNavigationHelper.getFiltersForNavigationItem({
            timeRange: timeRange,
            notAvailableFeatures: notAvailableFeatures,
            targets: targets,
            isPartner: Application.Main.company.isPartner(),
          }),
        },
      },
    ];

    navigationItems = NavigationHelper.removeUnnavigableItems(this.dependencies.Ext, navigationItems);

    let navigationHandler;
    if (canDrillDown) {
      navigationHandler = (event, navigationParams, section) => {
        if (BaseWidgetView.isSuccessState(event.currentTarget as Element)) {
          if (isBrowserFullscreen()) {
            exitFullscreen(() => {}, onExitFullscreenError.bind(this));
          }

          switch (section) {
            case NavigationConstants.NETWORK_V2.NAME:
              NavigationHelper.navigateToNetworkV2(navigationParams as NetworkGridAppExternalParams);
              break;
            case NavigationConstants.RISK_MANAGEMENT.NAME:
              NavigationHelper.navigateToRiskManagement(navigationParams as RiskManagementNavigationParams);
              break;
            case NavigationConstants.ALERTS.NAME:
              NavigationHelper.navigateToAlerts(this.dependencies.Ext, navigationParams as AlertsNavigationParams);
              break;
            default:
              throw new Error('Trying to navigate to invalid console module');
          }
        }
      };

      NavigationHelper.addDrillDownEffectsForMultipleElements(
        navigationItems.map(navigationItem => navigationItem.elements)
      );
    }

    NavigationHelper.updateNavigableItems(navigationItems, this.navigationHandlers, navigationHandler);
  }

  private getActiveEndpointsFilters(
    targets: Array<Target>,
    timeRange: number,
    isPartner: boolean
  ): Array<GridFilter<NetworkColumn>> {
    const filters: Array<GridFilter<NetworkColumn>> = [
      {
        type: {
          in: [
            Constants.Network.Filters.Types.COMPUTERS,
            Constants.Network.Filters.Types.VIRTUAL_MACHINES,
            Constants.Network.Filters.Types.CONTAINERS,
            Constants.Network.Filters.Types.GOLDEN_IMAGES,
          ],
        },
      },
      {
        managementStatus: {
          in: [ManagementStatus.MANAGED_NETWORK_V2],
        },
      },
      {
        lastSeen: {
          value: ExecutiveSummaryDrillDownHelper.convertTimeRange(timeRange),
        },
      },
    ];

    if (isPartner) {
      filters.push({
        company: {
          equals: targets[0].companyId,
        },
      });
    }

    return filters;
  }

  private getManagedEndpointsFilters(targets: Array<Target>, isPartner: boolean): Array<GridFilter<NetworkColumn>> {
    const filters: Array<GridFilter<NetworkColumn>> = [
      {
        type: {
          in: [
            Constants.Network.Filters.Types.COMPUTERS,
            Constants.Network.Filters.Types.VIRTUAL_MACHINES,
            Constants.Network.Filters.Types.CONTAINERS,
            Constants.Network.Filters.Types.GOLDEN_IMAGES,
          ],
        },
      },
      {
        managementStatus: {
          in: [ManagementStatus.MANAGED_NETWORK_V2],
        },
      },
    ];

    if (isPartner) {
      filters.push({
        company: {
          equals: targets[0].companyId,
        },
      });
    }
    return filters;
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getOverviewWidgetStrings();
  }

  setTranslatedStrings() {
    const { labelsMap } = this.translations;
    this.dom.$managedEndpointsTitle.innerHTML = labelsMap.managedEndpoints;
    this.dom.$activeEndpointsTitle.innerHTML = labelsMap.activeEndpoints;
    this.dom.$threatsBlockedTitle.innerHTML = labelsMap.threatsBlocked;
    this.dom.$companyScoreTitle.innerHTML = labelsMap.companyScore;
  }

  showLoader() {
    this.dom.$managedEndpointsTitle.innerHTML = this.translations.labelsMap.managedEndpoints;
    this.dom.$itemsList.forEach(el => {
      this.resetTemplateState(el);
      this.setTemplateState(el, TemplateState.LOADING);
    });
  }

  hideLoader() {
    this.dom.$itemsList.forEach(el => {
      this.resetTemplateState(el);
    });
  }

  buildSuccessView(response: BackendResponseWithMeta<GetOverviewInformationResponse>) {
    this.latestData = response.widgetResult;
    const sections = Object.keys(this.latestData);

    if (response[DASHBOARD_METADATA_KEY].displayMode === DisplayMode.PDF) {
      this.dom.$dashboard = document
        .querySelector('gz-dashboard-app')
        ?.shadowRoot?.querySelector('gz-dashboard') as DashboardWebComponent;
    } else {
      this.dom.$dashboard = document
        .querySelector('gz-generic-dashboard-app')
        ?.shadowRoot?.querySelector('gz-dashboard-macro-component') as GzDashboardMacroComponentWebComponent;
    }

    if (
      response[DASHBOARD_METADATA_KEY].displayMode !== DisplayMode.DASHBOARD ||
      (this.dom.$dashboard?.isPartner ?? Application.Main.company.isPartner()) ||
      !Application.Main.hasAnyRights([Rights.RIGHT_MANAGE_INVENTORY])
    ) {
      this.shouldDisplayIconsButtons = false;
      this.shouldDisplayInstallButtons = false;
    } else {
      this.shouldDisplayInstallButtons = this.latestData.managedEndpoints.status === Status.EMPTY_STATE;
      this.shouldDisplayIconsButtons = false;
    }
    if (sections.length === 0) {
      // If the response is malformed we need to ensure we don't build an empty succes view, like in other widgets
      this.buildErrorView();
    }

    for (const section of sections) {
      if (section === 'notAvailableFeatures') {
        continue;
      }

      const { status } = this.latestData[section];
      const item = this.shadow.querySelector(`[gz-overview="${section}"]`);

      this.resetTemplateState(item);
      this.resetTemplateState(item);

      if (section === 'activeEndpoints' && this.shouldDisplayInstallButtons) {
        this.setTemplateState(item, TemplateState.MISSING);
        continue;
      }

      if (status === Status.EMPTY_STATE) {
        this.handleEmptyDataState(section, item);
        continue;
      }

      if (status === Status.MISSING_LICENSE) {
        this.setTemplateState(item, TemplateState.MISSING);
        continue;
      }

      this.handleActiveState(section);
    }

    this.addDrillDownHandlers(
      response[DASHBOARD_METADATA_KEY].targets,
      response[DASHBOARD_METADATA_KEY].reportingPeriod.timeRange,
      response[DASHBOARD_METADATA_KEY].canDrillDown,
      this.latestData.notAvailableFeatures
    );
  }

  buildErrorView() {
    this.dom.$itemsList.forEach(item => {
      this.resetTemplateState(item);
      this.setTemplateState(item, TemplateState.ERROR);
      this.addItemEmptyState(item);
    });
  }

  private addItemEmptyState(item: HTMLElement, section?: string) {
    if (section === OverviewItems.MANAGED_ENDPOINTS) {
      this.dom.$managedEndpointsTitle.innerHTML = this.shouldDisplayInstallButtons
        ? this.translations.noEndpointsInstalledTitle
        : this.translations.labelsMap.managedEndpoints;
      this.dom.$managedEndpointsEmptyStateText.innerHTML = this.shouldDisplayInstallButtons
        ? this.translations.noEndpointsInstalledForCustomer
        : this.translations.noEndpointsInstalledForPartner;

      if (this.shouldDisplayInstallButtons && !this.installButtonsDisplayed) {
        this.setTemplateState(item, TemplateState.ENDPOINTS_EMPTY);
        this.addActionButtonsForCustomers(this.dom.$managedEndpointsEmptyState);
      }
    }

    const itemValue = item.querySelector('.item--value');
    const itemDelta = item.querySelector('.item--delta');

    if (itemValue) {
      itemValue.innerHTML = `<span class="error">--</span>`;
    }

    if (itemDelta) {
      itemDelta.innerHTML = '';
    }
  }

  private buildEmptyTemplate(section: string) {
    const item = this.shadow.querySelector(`[gz-overview="${section}"]`);
    this.addItemEmptyState(item, section);
  }

  private handleEmptyDataState(section: string, content: HTMLElement) {
    this.buildEmptyTemplate(section);
    this.setTemplateState(content, TemplateState.EMPTY);
  }

  private handleActiveState(section: string) {
    const $valueElement = this.shadow.querySelector(`[gz-overview="${section}"] [gz-overview-field="value"]`);

    // we don't show delta in the 'activeEndpoints' widget
    if (section === OverviewItems.ACTIVE_ENDPOINTS && this.latestData.activeEndpoints.data) {
      $valueElement.innerHTML = this.formatNumbers(this.latestData.activeEndpoints.data);
      return;
    }

    const { currentValue, delta } = this.latestData[section].data;
    const $deltaElement = this.shadow.querySelector(`[gz-overview="${section}"] [gz-overview-field="delta"]`);
    const $iconsContainer = this.shadow.querySelector('#managedEndpointsActions');

    $valueElement.innerHTML =
      section === OverviewItems.COMPANY_SCORE
        ? `${formatPercentageValue(currentValue)}%`
        : this.formatNumbers(currentValue);

    // Not all metrics come with delta
    const deltaStr = delta ? formatPercentages(delta) : undefined;

    if (section === OverviewItems.MANAGED_ENDPOINTS && this.shouldDisplayIconsButtons && !this.iconButtonsDisplayed) {
      this.addActionIconButtonsForCustomers($iconsContainer);
      setElementClass(this.dom.$managedEndpointsEmpty, 'hidden');
      setElementClass(this.dom.$managedEndpointsContainer, 'icon-action-buttons');
    }

    // We don't want to display 0%.
    if (!deltaStr || deltaStr === '0%') {
      $deltaElement.innerHTML = '';
      return;
    }

    $deltaElement.innerHTML = deltaStr;

    if (section === OverviewItems.MANAGED_ENDPOINTS) {
      this.applyDeltaClass($deltaElement, delta, true);
    }

    if (section === OverviewItems.COMPANY_SCORE || section === OverviewItems.THREATS_BLOCKED) {
      this.applyDeltaClass($deltaElement, delta, false);
    }
  }

  private formatNumbers(value: number): string {
    return this.localeManager.formatNumber(
      value,
      {
        notation: 'compact',
        compactDisplay: 'short',
      },
      // I.e., en_US -> en-US
      (htmlEncode(this.dependencies.getUserLanguage()) || '').replace(/_/g, '-')
    );
  }

  private addActionIconButtonsForCustomers($container: HTMLElement): void {
    const downloadIconButton = new GzButtonWebComponent();
    const downloadBtnTooltip = new GzTooltipWebComponent();
    downloadBtnTooltip.tooltipText = this.dependencies.translateFn('Install now');
    downloadIconButton.innerHTML = '<span class="icon ds-icon-download"></span>';
    downloadIconButton.type = ButtonTypes.OUTLINE;
    downloadIconButton.buttonHeight = ButtonHeights.SMALL;
    downloadIconButton.onclick = () =>
      this.dom.$managedEndpointsContainer.dispatchEvent(new CustomEvent(OverviewEvents.INSTALL_NOW));
    downloadBtnTooltip.appendChild(downloadIconButton);

    const sendLinksIconButton = new GzButtonWebComponent();
    const sendLinksBtnTooltip = new GzTooltipWebComponent();
    sendLinksBtnTooltip.tooltipText = this.dependencies.translateFn('Send download links');
    sendLinksIconButton.innerHTML = '<span class="icon ds-icon-resource_share_link"></span>';
    sendLinksIconButton.type = ButtonTypes.OUTLINE;
    sendLinksIconButton.buttonHeight = ButtonHeights.SMALL;
    sendLinksIconButton.onclick = () =>
      this.dom.$managedEndpointsContainer.dispatchEvent(new CustomEvent(OverviewEvents.SEND_DOWNLOAD_LINKS));
    sendLinksBtnTooltip.appendChild(sendLinksIconButton);

    $container.appendChild(downloadBtnTooltip);
    $container.appendChild(sendLinksBtnTooltip);

    this.iconButtonsDisplayed = true;
  }

  private addActionButtonsForCustomers($container: HTMLElement): void {
    const downloadButton = new GzButtonWebComponent();
    downloadButton.type = ButtonTypes.FULL;
    downloadButton.color = ButtonColors.PRIMARY;
    downloadButton.buttonHeight = ButtonHeights.SMALL;
    downloadButton.id = 'installNowButton';
    downloadButton.innerHTML = this.translations.installNowButton;
    downloadButton.onclick = () =>
      this.dom.$managedEndpointsContainer.dispatchEvent(new CustomEvent(OverviewEvents.INSTALL_NOW));

    const sendDownloadLinksButton = new GzButtonWebComponent();
    sendDownloadLinksButton.type = ButtonTypes.OUTLINE;
    sendDownloadLinksButton.color = ButtonColors.PRIMARY;
    sendDownloadLinksButton.buttonHeight = ButtonHeights.SMALL;
    sendDownloadLinksButton.id = 'sendDownloadLinksButton';
    sendDownloadLinksButton.innerHTML = this.translations.sendDownloadLinksButton;
    sendDownloadLinksButton.onclick = () =>
      this.dom.$managedEndpointsContainer.dispatchEvent(new CustomEvent(OverviewEvents.SEND_DOWNLOAD_LINKS));

    $container.appendChild(downloadButton);
    $container.appendChild(sendDownloadLinksButton);
    this.installButtonsDisplayed = true;
  }
}
